"use client";

import Image from "next/image";
import { Trans, useLingui } from "@lingui/react/macro";
import { Dialog, MediaPlayer } from "@marketing-sites/ui/primitives";

const VIDEO_URL = "https://fast.wistia.com/embed/medias/58l5qf80tx.m3u8";
const THUMBNAIL_SRC =
  "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/19e61a95e7593cdb6b95c032f944b80629cf3ba4-2500x1240.webp";
const BACKGROUND =
  "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/a8c0d2d24b54172115575a33ee77e2026ad4c694-1600x1198.webp";

export function CopilotVideoBanner() {
  const { t } = useLingui();
  const title = t`See Copilot in action`;
  const video = {
    url: VIDEO_URL,
    id: "copilot-demo",
    title,
    poster: THUMBNAIL_SRC,
  };
  const settingsLabels = {
    speed: t`Speed`,
    quality: t`Quality`,
    auto: t`Auto`,
  };

  return (
    <div
      data-analytics-region="copilot-video-banner"
      data-analytics-id="sec_helpdesk_copilot_video_banner"
      className="relative flex flex-col gap-4 overflow-hidden bg-[#c4cbd0] p-6 md:p-[1.875rem] lg:min-h-[14.375rem] lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:py-[1.125rem] lg:pr-[1.125rem]"
    >
      <div className="absolute left-[32.73%] top-[-232.07%] hidden h-[353.44%] w-[86.11%] md:block">
        <Image
          src={BACKGROUND}
          alt=""
          fill
          sizes="90vw"
          className="pointer-events-none object-cover"
        />
      </div>

      <div className="text-off-black relative flex max-w-[35.5rem] flex-col gap-6">
        <h2 className="text-heading-4 text-balance">
          <Trans>See the newest Copilot features announced at Pioneer</Trans>
        </h2>
        <p className="text-body-lg text-off-black/80 max-w-[26.5rem]">
          <Trans>
            Watch Paul Adams&rsquo; Pioneer keynote for a first look at how
            Copilot works proactively on every conversation, pulling in context,
            taking action across your connected systems and handling the admin,
            so your team resolves more.
          </Trans>
        </p>
      </div>

      <div className="relative aspect-[340/197] w-full shrink-0 lg:hidden">
        <MediaPlayer.Context video={video} overlay={{ hideTitle: true }}>
          <MediaPlayer.Root>
            <MediaPlayer.Video
              analyticsId="evt_helpdesk_copilot_video_banner"
              playsInline
            />
            <MediaPlayer.Overlay playLabel={t`Play`} />
            <MediaPlayer.Controls />
            <MediaPlayer.SettingsMenu labels={settingsLabels} />
          </MediaPlayer.Root>
        </MediaPlayer.Context>
      </div>

      <Dialog.Root
        wrapperClassName="relative hidden shrink-0 lg:block"
        openMetadata={{
          analyticsId: "modal_helpdesk_copilot_video_banner_opened",
        }}
        closeMetadata={{
          analyticsId: "modal_helpdesk_copilot_video_banner_closed",
        }}
      >
        <Dialog.Trigger
          aria-label={title}
          className="group relative block aspect-[340/197] w-[21.25rem] cursor-pointer overflow-hidden"
        >
          <Image
            src={THUMBNAIL_SRC}
            alt=""
            fill
            sizes="340px"
            className="object-cover object-left transition-transform duration-300 group-hover:scale-105"
          />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90 p-4 text-black">
            <MediaPlayer.PlayIcon aria-hidden className="w-4" />
          </span>
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Backdrop />
          <Dialog.Viewport>
            <Dialog.Popup
              aria-label={title}
              className="bg-off-black w-[min(64rem,calc(100vw-2rem))]"
              closeButton={
                <Dialog.Close
                  aria-label={t`Close`}
                  className="text-button-sm absolute right-4 top-4 z-10 cursor-pointer text-white underline"
                >
                  {t`Close`}
                </Dialog.Close>
              }
            >
              <div className="relative aspect-video w-full">
                <MediaPlayer.Context video={video}>
                  <MediaPlayer.Root>
                    <MediaPlayer.Video
                      autoplay
                      playsInline
                      analyticsId="evt_helpdesk_copilot_video_banner_modal"
                    />
                    <MediaPlayer.Overlay playLabel={t`Play`} />
                    <MediaPlayer.Controls />
                    <MediaPlayer.SettingsMenu labels={settingsLabels} />
                  </MediaPlayer.Root>
                </MediaPlayer.Context>
              </div>
            </Dialog.Popup>
          </Dialog.Viewport>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
